import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Chrono } from 'react-chrono';
import { getRotableById } from '../api/rotablesAPI';
import { getRotableTransactions } from '../api/rotable-transactionsAPI';
import { formatIntDate, parseUtc } from '../utils/dateUtils';
import './RotableViewModal.css';

const formatValue = (value) => (value === null || value === undefined || value === '' ? '—' : value);

const RotableViewModal = ({ show, rotableId, onClose, initialRotable }) => {
  const [rotable, setRotable] = useState(initialRotable || null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const handleImageClick = useCallback((src) => {
    setImageModalSrc(src);
    setShowImageModal(true);
  }, []);

  const formatTime = useCallback((value) => {
    if (!value) return '';
    const date = parseUtc(value);
    if (Number.isNaN(date.getTime())) return '';
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${min}`;
  }, []);

  useEffect(() => {
    if (!show || !rotableId) return;
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [rotableData, transactionData] = await Promise.all([
          getRotableById(rotableId),
          getRotableTransactions({ rotableId }),
        ]);
        if (!isMounted) return;
        setRotable(rotableData);
        setTransactions(Array.isArray(transactionData) ? transactionData : []);
      } catch (err) {
        console.error('Error loading rotable details', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [rotableId, show]);

  useEffect(() => {
    if (!show) return;
    setRotable(initialRotable || null);
  }, [initialRotable, show]);

  const infoRows = useMemo(() => {
    const location = [rotable?.zone, rotable?.aisle, rotable?.bay, rotable?.shelf]
      .filter(Boolean)
      .join(' / ');
    const warrantyLabel = rotable?.warrantyStart || rotable?.warrantyEnd
      ? `${formatIntDate(rotable?.warrantyStart)} - ${formatIntDate(rotable?.warrantyEnd)}`
      : '—';
    const stockItemLabel =
      rotable?.stockItem?.materialNumber ||
      rotable?.stockItem?.name ||
      rotable?.stockItem?.stockItem ||
      rotable?.stockItemId ||
      '—';

    return [
      { label: 'Serial Number', value: formatValue(rotable?.serialNumber) },
      { label: 'Part Number', value: formatValue(rotable?.part?.partNumber ?? rotable?.partNumber) },
      { label: 'Stock Item', value: formatValue(stockItemLabel) },
      { label: 'Status', value: formatValue(rotable?.status?.name) },
      { label: 'Repair Status', value: formatValue(rotable?.repairStatus?.name) },
      { label: 'Component Type', value: formatValue(rotable?.componentType?.name) },
      { label: 'Location', value: formatValue(location || '—') },
      { label: 'Number of Turns', value: formatValue(rotable?.numberOfTurns) },
      { label: 'Manufacture Date', value: formatValue(formatIntDate(rotable?.manufactureDate)) },
      { label: 'Warranty Information', value: formatValue(warrantyLabel) },
      { label: 'Notes', value: formatValue(rotable?.notes) },
    ];
  }, [rotable]);

  const sortedTransactions = useMemo(() => {
    const items = Array.isArray(transactions) ? transactions.slice() : [];
    return items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [transactions]);

  const timelineItems = useMemo(
    () =>
      sortedTransactions.map((transaction) => ({
        title: `${formatIntDate(transaction.createdAt)}\n${formatTime(transaction.createdAt)}`,
        cardTitle: transaction.rotableStatus?.name ?? 'Status Update',
        cardSubtitle: `${transaction.repairStatus?.name ?? '—'}`,
      })),
    [formatTime, sortedTransactions]
  );

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Rotable Details</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="rotable-view-modal__layout">
              <div className="rotable-view-modal__details">
                {infoRows.map((row) => (
                  <div key={row.label} className="rotable-view-modal__detail-row">
                    <div className="text-muted text-uppercase small">{row.label}</div>
                    <div className="fw-semibold">{row.value}</div>
                  </div>
                ))}
              </div>
              <div className="rotable-view-modal__timeline-wrapper">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0 text-lg my-12">Component Story</h6>
                  {loading && <span className="spinner-border spinner-border-sm" />}
                </div>
                <div className="rotable-view-modal__timeline-container">
                  {!loading && sortedTransactions.length === 0 ? (
                    <div className="text-muted rotable-view-modal__timeline-empty">
                      No transactions recorded.
                    </div>
                  ) : (
                    <div className="rotable-view-modal__timeline">
                      <Chrono
                        items={timelineItems}
                        mode="VERTICAL"
                        cardHeight={220}
                        disableToolbar
                        allowDynamicUpdate
                        theme={{
                          primary: "#2f80ed",
                          secondary: "#f8f9fa",
                          cardBgColor: "#ffffff",
                          cardForeColor: "#111827",
                          titleColor: "#6b7280",
                        }}
                      >
                        {sortedTransactions.map((transaction, index) => {
                          const detailRows = [
                            {
                              label: 'Asset',
                              value: transaction.asset?.assetNumber,
                              shouldRender: Boolean(transaction.asset?.assetNumber),
                            },
                            {
                              label: 'Task',
                              value: transaction.task?.name
                                ? `${transaction.task?.code?.code} - ${transaction.task?.name}`
                                : null,
                              shouldRender: Boolean(transaction.task?.name),
                            },
                            {
                              label: 'Work Order',
                              value: transaction.workOrder?.workOrderNumber,
                              shouldRender: Boolean(transaction.workOrder?.workOrderNumber),
                            }
                          ].filter((row) => row.shouldRender);

                          const images = Array.isArray(transaction.images) ? transaction.images : [];

                          return (
                            <div key={transaction.id ?? transaction.createdAt ?? index}>
                              <div className="rotable-view-modal__timeline-card">
                                {detailRows.map((row) => (
                                  <div
                                    key={row.label}
                                    className="rotable-view-modal__timeline-detail"
                                  >
                                    <span className="text-muted">{row.label}:</span>
                                    <span>{row.value}</span>
                                  </div>
                                ))}

                                {images.length > 0 && (
                                  <div className="rotable-view-modal__timeline-images mb-12">
                                    {images.map((img, imgIndex) => (
                                      <button
                                        key={`${img}-${imgIndex}`}
                                        type="button"
                                        className="rotable-view-modal__timeline-image-button"
                                        onClick={() =>
                                          handleImageClick(
                                            `${process.env.REACT_APP_SPACES_UPLOAD}/${img}`,
                                          )
                                        }
                                      >
                                        <img
                                          src={`${process.env.REACT_APP_SPACES_BASE}/${img}`}
                                          alt="Transaction"
                                          className="rotable-view-modal__timeline-image"
                                        />
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="rotable-view-modal__timeline-detail">
                                  <span>
                                    {transaction.notes?.length > 100
                                      ? `${transaction.notes.slice(0, 100)}…`
                                      : transaction.notes}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </Chrono>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
      {showImageModal && (
        <div
          className="modal modal-backdrop show d-block"
          onClick={() => setShowImageModal(false)}
          tabIndex="-1"
        >
          <div className="modal-dialog" style={{ maxWidth: '75%' }}>
            <div className="modal-content" onClick={(event) => event.stopPropagation()}>
              <img src={imageModalSrc} alt="view" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

RotableViewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  rotableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  initialRotable: PropTypes.shape({}),
};

RotableViewModal.defaultProps = {
  rotableId: null,
  initialRotable: null,
};

export default RotableViewModal;
